import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ChevronRight,
  ClipboardList,
  FlaskConical,
  Loader2,
  RefreshCw,
  Search,
  Undo2,
} from "lucide-react";
import {
  timetableDataQualityApi,
  type BatchInfo,
  type DataQualityCategory,
  type DataQualityItem,
  type DataQualityResponse,
  type DataQualityStats,
  type KenntnisGroup,
  type KenntnisPreviewResponse,
} from "@/services/api/timetableDataQuality";
import { extractErrorMessage } from "@/utils/errorHandler";
import {
  clearPersistedBatches,
  usePersistedBatch,
  type PersistedBatchState,
} from "@/hooks/usePersistedBatch";
import { BatchRecoveryBanner } from "@/components/timetable-import/BatchRecoveryBanner";
import { SystemTimetableModal } from "@/components/dataquality/SystemTimetableModal";
import { PdfTimetableModal } from "@/components/dataquality/PdfTimetableModal";
import { CatalogEvidencePanel } from "@/components/dataquality/CatalogEvidencePanel";
import { TokenCleanupTab } from "@/components/dataquality/TokenCleanupTab";
import { CodeConceptTrainer } from "@/components/dataquality/CodeConceptTrainer";
import { KenntnisSealModal } from "@/components/dataquality/KenntnisSealModal";
import {
  FindingFixPanel,
  REASON_LABELS,
  type ScanFinding,
} from "@/components/dataquality/FindingFixPanel";
import { ProgressDialog } from "@/components/common/ProgressDialog";
import { BulkImportProgress } from "@/components/timetable-import/BulkImportProgress";
import { BulkShopDialog } from "@/components/timetable-import/BulkShopDialog";
import { TeacherLetterDirectory } from "@/components/teacher/TeacherLetterDirectory";
import { TeacherCode } from "@/components/teacher/TeacherCode";
import { ImportValidityDialog } from "@/components/timetable-import/ImportValidityDialog";
import { bulkTimetableImportApi } from "@/services/api/bulkTimetableImport";
import { timetableImportV2Api } from "@/services/api/timetableImportV2";
import { nextContinueAction } from "@/components/timetable-import/continueBatch";

type CategoryFilter = "all" | DataQualityCategory;
type PanelTab =
  "findings" | "values" | "tokens" | "class_concepts" | "room_concepts";

const CATEGORY_LABELS: Record<DataQualityCategory, string> = {
  subject: "Fach",
  room: "Raum",
  klass: "Klasse",
};

const CATEGORY_COLORS: Record<DataQualityCategory, string> = {
  subject: "bg-blue-100 text-blue-800",
  room: "bg-purple-100 text-purple-800",
  klass: "bg-emerald-100 text-emerald-800",
};

function splitFullName(name: string | null | undefined): {
  first_name: string;
  last_name: string;
} {
  const raw = (name || "").trim();
  if (!raw) return { first_name: "", last_name: "" };
  const parts = raw.split(/\s+/);
  if (parts.length === 1) return { first_name: "", last_name: parts[0] ?? "" };
  return {
    first_name: parts.slice(0, -1).join(" "),
    last_name: parts[parts.length - 1] ?? "",
  };
}

function toDirectoryTeacher(t: {
  teacher_id: string;
  name: string | null;
  code: string | null;
  first_name?: string | null;
  last_name?: string | null;
}) {
  const parsed = splitFullName(t.name);
  return {
    id: t.teacher_id,
    last_name: t.last_name?.trim() || parsed.last_name,
    first_name: t.first_name?.trim() || parsed.first_name,
    teacher_code: t.code,
  };
}

function confidenceColor(confidence: number): string {
  if (confidence >= 0.9) return "bg-green-500";
  if (confidence >= 0.6) return "bg-yellow-500";
  return "bg-gray-400";
}

export default function TimetableDataQualityPage() {
  const navigate = useNavigate();
  const { addBatch } = usePersistedBatch();
  const [tab, setTab] = useState<PanelTab>("findings");
  const [findings, setFindings] = useState<Awaited<
    ReturnType<typeof timetableDataQualityApi.getFindings>
  > | null>(null);
  const [category, setCategory] = useState<CategoryFilter>("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [data, setData] = useState<DataQualityResponse | null>(null);
  const [stats, setStats] = useState<DataQualityStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [skip, setSkip] = useState(0);
  const limit = 50;

  const fetchGenRef = useRef(0);
  const validityRef = useRef<{ from: string | null; to: string | null }>({
    from: null,
    to: null,
  });
  const [lastBatchId, setLastBatchId] = useState<string | null>(null);
  const [lastSpanUndoId, setLastSpanUndoId] = useState<string | null>(null);

  // Batches
  const [batches, setBatches] = useState<BatchInfo[]>([]);

  const [undoBusyId, setUndoBusyId] = useState<string | null>(null);

  const [systemModalTeacher, setSystemModalTeacher] = useState<string | null>(
    null
  );
  const [pdfModalTeacher, setPdfModalTeacher] = useState<string | null>(null);
  const [evidenceItem, setEvidenceItem] = useState<DataQualityItem | null>(
    null
  );
  const [findingsReady, setFindingsReady] = useState(false);
  const [fixPanel, setFixPanel] = useState<{
    teacherId: string;
    teacherName: string;
    finding: ScanFinding;
  } | null>(null);
  const [reimportConfirm, setReimportConfirm] = useState(false);
  const [forceRestart, setForceRestart] = useState(false);
  const [showValidity, setShowValidity] = useState(false);
  const [importJob, setImportJob] = useState<{
    jobId: string;
    batchId: string;
  } | null>(null);
  const [shopBatchId, setShopBatchId] = useState<string | null>(null);
  const [importStarting, setImportStarting] = useState(false);
  const [shopSkippedBatchId, setShopSkippedBatchId] = useState<string | null>(
    null
  );
  const [okTeachersOpen, setOkTeachersOpen] = useState(false);
  const [openQuery, setOpenQuery] = useState("");
  const [okQuery, setOkQuery] = useState("");
  const [tokenTick, setTokenTick] = useState(0);
  const [kenntnis, setKenntnis] = useState<KenntnisPreviewResponse | null>(
    null
  );
  const [kenntnisOpen, setKenntnisOpen] = useState(false);
  const [kenntnisBusy, setKenntnisBusy] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [catalogWipeBusy, setCatalogWipeBusy] = useState(false);
  const [memoryOpen, setMemoryOpen] = useState(false);
  const [memoryWipeBusy, setMemoryWipeBusy] = useState(false);

  const fetchFindings = useCallback(async () => {
    try {
      const response = await timetableDataQualityApi.getFindings();
      setFindings(response);
    } catch (err) {
      setError(
        extractErrorMessage(err, {
          loadError: "Befunde konnten nicht geladen werden",
        })
      );
    } finally {
      setFindingsReady(true);
    }
  }, []);

  const fetchKenntnis = useCallback(async () => {
    try {
      const response = await timetableDataQualityApi.getKenntnisPreview();
      setKenntnis(response);
    } catch (err) {
      setError(
        extractErrorMessage(err, {
          loadError: "Kenntnis konnte nicht geladen werden",
        })
      );
    }
  }, []);

  useEffect(() => {
    fetchFindings();
    fetchKenntnis();
  }, [fetchFindings, fetchKenntnis]);

  const fetchData = useCallback(async () => {
    const gen = ++fetchGenRef.current;
    setLoading(true);
    setError(null);
    try {
      const response = await timetableDataQualityApi.getDataQuality({
        category,
        search: debouncedSearch || undefined,
        skip,
        limit,
      });
      if (gen !== fetchGenRef.current) return; // stale response — discard
      setData(response);
      setStats(response.stats);
      // data-fix v4 (N7): clamp skip when the result shrank (e.g. after an
      // action removed groups) so the page cannot sit on an empty page
      if (response.total <= skip && skip > 0) {
        setSkip(Math.max(0, (Math.ceil(response.total / limit) - 1) * limit));
      }
    } catch (err) {
      if (gen !== fetchGenRef.current) return;
      setError(
        extractErrorMessage(err, {
          loadError: "Datenqualitäts-Daten konnten nicht geladen werden",
        })
      );
    } finally {
      if (gen === fetchGenRef.current) setLoading(false);
    }
  }, [category, debouncedSearch, skip]);

  const fetchBatches = useCallback(async () => {
    try {
      const response = await timetableDataQualityApi.listBatches();
      setBatches(response.batches);
    } catch (err) {
      // data-fix v4 (N7): surface instead of silently swallowing
      setError(
        extractErrorMessage(err, {
          loadError: "Batch-Historie konnte nicht geladen werden",
        })
      );
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // data-fix v4 (N4): debounce the search input (300ms) — one request per
  // pause instead of one per keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // data-fix v4 (N7): auto-dismiss the success banner (undo stays available
  // in the batch history)
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => {
      setSuccess(null);
      setLastBatchId(null);
    }, 10000);
    return () => clearTimeout(t);
  }, [success]);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  const itemKey = (item: DataQualityItem) =>
    `${item.category}:${item.value ?? "<null>"}`;

  const openFinding = (
    teacherId: string,
    teacherName: string,
    finding: ScanFinding
  ) => {
    setFixPanel({ teacherId, teacherName, finding });
  };

  const pickNextFinding = (
    teachers: NonNullable<typeof findings>["teachers"],
    teacherId: string,
    closedEntryId: string,
    skipCurrent = false
  ) => {
    const idx = teachers.findIndex((t) => t.teacher_id === teacherId);
    const same = idx >= 0 ? teachers[idx] : undefined;
    if (!skipCurrent) {
      const stillOpen = (same?.findings || []).find(
        (f) => f.entry_id === closedEntryId
      );
      if (same && stillOpen) {
        return {
          teacherId: same.teacher_id,
          teacherName: same.name || same.code || same.teacher_id,
          finding: stillOpen as ScanFinding,
        };
      }
    }
    const remaining = (same?.findings || []).filter(
      (f) => f.entry_id !== closedEntryId
    );
    if (same && remaining[0]) {
      return {
        teacherId: same.teacher_id,
        teacherName: same.name || same.code || same.teacher_id,
        finding: remaining[0] as ScanFinding,
      };
    }
    for (let i = idx + 1; i < teachers.length; i += 1) {
      const t = teachers[i]!;
      if (t.findings.length > 0) {
        return {
          teacherId: t.teacher_id,
          teacherName: t.name || t.code || t.teacher_id,
          finding: t.findings[0] as ScanFinding,
        };
      }
    }
    return null;
  };

  const openNextFinding = () => {
    if (!fixPanel) {
      setFixPanel(null);
      return;
    }
    const next = pickNextFinding(
      findings?.teachers || [],
      fixPanel.teacherId,
      fixPanel.finding.entry_id,
      true
    );
    setFixPanel(next);
  };

  const continueInterruptedImport = async (batch: PersistedBatchState) => {
    setError(null);
    try {
      const status = await timetableImportV2Api.getBatchStatus(batch.batchId);
      const action = nextContinueAction(
        status.status,
        status.sections || [],
        false
      );
      if (action.type === "shop") {
        setShopBatchId(batch.batchId);
        return;
      }
      if (action.type === "import") {
        await timetableImportV2Api.importAllSections(batch.batchId);
        await fetchFindings();
        return;
      }
      if (action.type === "assignment" || action.type === "token_matching") {
        navigate(`/superadmin/timetable-import?continueBatch=${batch.batchId}`);
        return;
      }
      const resumed = await bulkTimetableImportApi.resumeBatch(batch.batchId);
      if (resumed.batch_status === "pending_bulk_shop") {
        setShopBatchId(batch.batchId);
        return;
      }
      if (
        resumed.batch_status === "resolved" ||
        resumed.batch_status === "partially_failed"
      ) {
        await timetableImportV2Api.importAllSections(batch.batchId);
        await fetchFindings();
        return;
      }
      if (resumed.batch_status === "pending_assignment") {
        navigate(`/superadmin/timetable-import?continueBatch=${batch.batchId}`);
        return;
      }
      if (resumed.batch_status === "resolving") {
        navigate(`/superadmin/timetable-import?continueBatch=${batch.batchId}`);
        return;
      }
      if (resumed.job_id) {
        setImportJob({ jobId: resumed.job_id, batchId: batch.batchId });
        return;
      }
      setError("Unterbrochenen Import konnte nicht fortgesetzt werden");
    } catch (err) {
      setError(
        extractErrorMessage(err, {
          loadError: "Unterbrochenen Import konnte nicht geöffnet werden",
        })
      );
    }
  };

  const startReimport = async (
    validFrom: string | null,
    validTo: string | null,
    restart: boolean
  ) => {
    const fileId = findings?.import_file_id;
    if (!fileId) {
      setError(
        "Kein PDF zum Neuimport. Datei unter Stundenplan-Import wählen."
      );
      return;
    }
    validityRef.current = { from: validFrom, to: validTo };
    setImportStarting(true);
    setError(null);
    try {
      const result = await bulkTimetableImportApi.startImport({
        uploaded_file_id: fileId,
        valid_from: validFrom,
        valid_to: validTo,
        force_restart: restart || undefined,
      });
      setShowValidity(false);
      setReimportConfirm(false);
      setForceRestart(false);
      setImportJob({ jobId: result.job_id, batchId: result.batch_id });
      addBatch({
        batchId: result.batch_id,
        jobId: result.job_id,
        fileId,
        fileName: "Neuimport",
        startedAt: new Date().toISOString(),
        lastUpdated: new Date().toISOString(),
        status: "extracting",
        totalSections: 0,
        resolvedSections: 0,
        unresolvedSessionId: null,
      });
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 409 && !restart) {
        setForceRestart(true);
        setShowValidity(false);
      } else {
        setError(
          extractErrorMessage(err, {
            loadError: "Import konnte nicht gestartet werden",
          })
        );
      }
    } finally {
      setImportStarting(false);
    }
  };

  const handleMemoryWipe = async () => {
    if (memoryWipeBusy) return;
    setMemoryWipeBusy(true);
    setError(null);
    try {
      await timetableDataQualityApi.wipeMemory();
      clearPersistedBatches();
      setMemoryOpen(false);
      setSuccess("Token-Speicher gelöscht");
      setLastBatchId(null);
      await Promise.all([
        fetchFindings(),
        fetchData(),
        fetchBatches(),
        fetchKenntnis(),
      ]);
      setTokenTick((n) => n + 1);
    } catch (err) {
      setError(
        extractErrorMessage(err, {
          loadError: "Token-Speicher konnte nicht gelöscht werden",
        })
      );
    } finally {
      setMemoryWipeBusy(false);
    }
  };

  const handleCatalogWipe = async () => {
    if (catalogWipeBusy) return;
    setCatalogWipeBusy(true);
    setError(null);
    try {
      await timetableDataQualityApi.wipeCatalog();
      clearPersistedBatches();
      setCatalogOpen(false);
      setSuccess("Katalog und Token-Speicher gelöscht");
      setLastBatchId(null);
      await Promise.all([
        fetchFindings(),
        fetchData(),
        fetchBatches(),
        fetchKenntnis(),
      ]);
      setTokenTick((n) => n + 1);
    } catch (err) {
      setError(
        extractErrorMessage(err, { loadError: "Katalog-Wipe fehlgeschlagen" })
      );
    } finally {
      setCatalogWipeBusy(false);
    }
  };

  const handleSpanUndo = async (undoId: string) => {
    if (undoBusyId) return;
    setUndoBusyId(undoId);
    setError(null);
    try {
      await timetableDataQualityApi.undoExtend({ undo_id: undoId });
      setSuccess("Erweiterung zurückgenommen");
      setLastSpanUndoId(null);
      await Promise.all([fetchFindings(), fetchData()]);
    } catch (err) {
      setError(
        extractErrorMessage(err, {
          loadError: "Rückgängig machen fehlgeschlagen",
        })
      );
    } finally {
      setUndoBusyId(null);
    }
  };

  const handleUndo = async (batchId: string) => {
    if (undoBusyId) return;
    setUndoBusyId(batchId);
    setError(null);
    try {
      const result = await timetableDataQualityApi.bulkUndo(batchId);
      setSuccess(
        `Batch ${batchId.slice(0, 8)}… zurückgenommen: ${result.restored_rows} Einträge wiederhergestellt`
      );
      setLastBatchId(null);
      await Promise.all([fetchFindings(), fetchData(), fetchBatches()]);
      setTokenTick((n) => n + 1);
    } catch (err) {
      setError(
        extractErrorMessage(err, {
          loadError: "Rückgängig machen fehlgeschlagen",
        })
      );
    } finally {
      setUndoBusyId(null);
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / limit)) : 1;
  const currentPage = Math.floor(skip / limit) + 1;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ClipboardList className="w-6 h-6" />
            Datenqualität Stundenplan
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Lücken hier schliessen. Double nach oben oder unten erweitern oder
            fusionieren.
          </p>
          {kenntnis && (
            <p className="text-sm text-gray-600 mt-1">
              Kenntnis: {kenntnis.live.ignore} Ignore · {kenntnis.live.mapped}{" "}
              zugeordnet · {kenntnis.live.split} Split · Muster live
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setKenntnisOpen(true)}
            disabled={
              !kenntnis ||
              Object.values(kenntnis.delta).every(
                (items) => (items || []).length === 0
              )
            }
            className="px-3 py-2 text-sm font-medium text-blue-800 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-50"
          >
            Kenntnis speichern
          </button>
          <button
            onClick={() => setReimportConfirm(true)}
            disabled={!findings?.import_available}
            className="px-3 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50"
          >
            Neu importieren
          </button>
          <button
            onClick={() => {
              fetchFindings();
              fetchData();
              fetchBatches();
              fetchKenntnis();
            }}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Aktualisieren
          </button>
        </div>
      </div>

      <BatchRecoveryBanner
        onContinue={(batch) => void continueInterruptedImport(batch)}
      />

      {/* Tab nav */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setTab("findings")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            tab === "findings"
              ? "border-blue-600 text-blue-600"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          Befunde
        </button>
        <button
          onClick={() => setTab("values")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            tab === "values"
              ? "border-blue-600 text-blue-600"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          Katalog
        </button>
        <button
          onClick={() => setTab("tokens")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            tab === "tokens"
              ? "border-blue-600 text-blue-600"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          Tokens
        </button>
        <button
          onClick={() => setTab("class_concepts")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-1 ${
            tab === "class_concepts"
              ? "border-blue-600 text-blue-600"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          <FlaskConical className="w-4 h-4" />
          Klassen-Code-Muster
        </button>
        <button
          onClick={() => setTab("room_concepts")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            tab === "room_concepts"
              ? "border-blue-600 text-blue-600"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          Raum-Code-Muster
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm flex items-center justify-between">
          <span>{success}</span>
          {(lastSpanUndoId || lastBatchId) && (
            <button
              onClick={() =>
                lastSpanUndoId
                  ? handleSpanUndo(lastSpanUndoId)
                  : handleUndo(lastBatchId!)
              }
              disabled={undoBusyId !== null}
              className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-green-800 border border-green-300 rounded hover:bg-green-100 disabled:opacity-50"
            >
              <Undo2 className="w-3 h-3" />
              {undoBusyId ? "…" : "Rückgängig"}
            </button>
          )}
        </div>
      )}

      {tab === "findings" && (
        <div className="space-y-3">
          <div className="bg-blue-50 border border-blue-200 text-blue-900 px-4 py-3 rounded-lg text-sm">
            Lücken an der Karte schliessen (Fach zuordnen, leer bestätigen,
            Klasse auftrennen). Double: Zelle nach oben oder unten erweitern
            oder fusionieren.
          </div>
          {findings?.heuristic_banner && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg text-sm">
              Alte Importe ohne PDF-Snapshot: Ampel per Heuristik. Gelb nur nach
              Neuimport.
            </div>
          )}
          {shopSkippedBatchId && (
            <div className="bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 rounded-lg text-sm flex items-center justify-between gap-3">
              <span>
                Shop offen — Batch nicht fertig, Befunde noch vom letzten
                Persist.
              </span>
              <button
                type="button"
                className="shrink-0 px-3 py-1 text-xs font-medium text-amber-900 border border-amber-300 rounded hover:bg-amber-100"
                onClick={() => {
                  setShopBatchId(shopSkippedBatchId);
                  setShopSkippedBatchId(null);
                }}
              >
                Shop öffnen
              </button>
            </div>
          )}
          {(findings?.teachers || []).length === 0 && (
            <p className="text-sm text-gray-500">
              Keine Befunde für den letzten Import.
            </p>
          )}
          {(() => {
            const teachers = findings?.teachers || [];
            const openTeachers = teachers.filter(
              (t) => !t.manually_confirmed && (t.red > 0 || t.yellow > 0)
            );
            const okTeachers = teachers.filter(
              (t) => t.manually_confirmed || (t.red === 0 && t.yellow === 0)
            );
            const qOpen = openQuery.trim().toLowerCase();
            const qOk = okQuery.trim().toLowerCase();
            const openItems = openTeachers
              .map(toDirectoryTeacher)
              .filter(
                (t) =>
                  !qOpen ||
                  t.last_name.toLowerCase().includes(qOpen) ||
                  t.first_name.toLowerCase().includes(qOpen) ||
                  (t.teacher_code || "").toLowerCase().includes(qOpen)
              );
            const okItems = okTeachers
              .map(toDirectoryTeacher)
              .filter(
                (t) =>
                  !qOk ||
                  t.last_name.toLowerCase().includes(qOk) ||
                  t.first_name.toLowerCase().includes(qOk) ||
                  (t.teacher_code || "").toLowerCase().includes(qOk)
              );
            const selectedOpen =
              openTeachers.find((t) => t.teacher_id === systemModalTeacher) ??
              openTeachers[0];
            const selectedDir = selectedOpen
              ? toDirectoryTeacher(selectedOpen)
              : null;
            return (
              <div className="grid min-h-[480px] grid-cols-1 md:grid-cols-[minmax(260px,320px)_1fr] gap-3">
                <div className="rounded-xl border border-gray-200 bg-white p-3.5">
                  <TeacherLetterDirectory
                    title="Offen"
                    items={openItems}
                    query={openQuery}
                    onQueryChange={setOpenQuery}
                    selectedId={systemModalTeacher ?? selectedOpen?.teacher_id}
                    onSelect={(item) => setSystemModalTeacher(item.id)}
                  />
                </div>
                <div className="space-y-3 min-w-0">
                  {selectedOpen && (
                    <div className="border rounded-lg p-3">
                      <div className="flex items-center justify-between gap-2">
                        <button
                          type="button"
                          className="flex min-w-0 items-center gap-2.5 text-left"
                          onClick={() =>
                            setSystemModalTeacher(selectedOpen.teacher_id)
                          }
                        >
                          <TeacherCode code={selectedOpen.code} />
                          <span className="min-w-0 truncate text-[13px] font-semibold">
                            {selectedDir?.last_name}
                            {selectedDir?.first_name
                              ? `, ${selectedDir.first_name}`
                              : ""}
                          </span>
                        </button>
                        <div className="text-xs space-x-2 shrink-0">
                          <span className="text-red-600">
                            {selectedOpen.red} rot
                          </span>
                          <span className="text-amber-600">
                            {selectedOpen.yellow} gelb
                          </span>
                          <span className="text-green-600">
                            {selectedOpen.green} grün
                          </span>
                          <button
                            type="button"
                            className="text-blue-600 hover:underline"
                            onClick={() =>
                              setSystemModalTeacher(selectedOpen.teacher_id)
                            }
                          >
                            Stundenplan
                          </button>
                          <button
                            type="button"
                            className="text-blue-600 hover:underline"
                            onClick={() =>
                              setPdfModalTeacher(selectedOpen.teacher_id)
                            }
                          >
                            PDF
                          </button>
                        </div>
                      </div>
                      {selectedOpen.findings.map((f) => (
                        <button
                          type="button"
                          key={f.entry_id}
                          className="block text-xs text-gray-600 mt-1 text-left hover:underline"
                          onClick={() =>
                            openFinding(
                              selectedOpen.teacher_id,
                              selectedOpen.name ||
                                selectedOpen.code ||
                                selectedOpen.teacher_id,
                              f as ScanFinding
                            )
                          }
                        >
                          L{f.lesson_number ?? "?"} ·{" "}
                          {REASON_LABELS[f.reasons[0] || ""] ||
                            f.reasons.join(", ")}{" "}
                          · {f.klass_name || ""} {f.subject_name || ""}
                        </button>
                      ))}
                    </div>
                  )}
                  {okTeachers.length > 0 && (
                    <div className="rounded-xl border border-gray-200 bg-white">
                      <button
                        type="button"
                        className="w-full text-left px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
                        onClick={() => setOkTeachersOpen((open) => !open)}
                      >
                        {okTeachersOpen ? "▾" : "▸"} Erledigt (
                        {okTeachers.length})
                      </button>
                      {okTeachersOpen && (
                        <div className="px-3.5 pb-3.5">
                          <TeacherLetterDirectory
                            items={okItems}
                            query={okQuery}
                            onQueryChange={setOkQuery}
                            onSelect={(item) => setSystemModalTeacher(item.id)}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {tab === "findings" && batches.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            Batch-Historie (Rückgängig unbegrenzt)
          </h3>
          <div className="space-y-2">
            {batches.map((batch) => (
              <div
                key={batch.batch_id}
                className="flex items-center justify-between text-sm"
              >
                <span className="font-mono text-xs text-gray-500">
                  {batch.batch_id.slice(0, 13)}…
                </span>
                <span className="text-gray-600">
                  {batch.created_at
                    ? new Date(batch.created_at).toLocaleString("de-CH", {
                        dateStyle: "short",
                        timeStyle: "short",
                      })
                    : "—"}{" "}
                  · {batch.affected_entries_count} Einträge · {batch.status}
                  {batch.is_reverted && " · zurückgenommen"}
                </span>
                {batch.can_undo && (
                  <button
                    onClick={() => handleUndo(batch.batch_id)}
                    disabled={undoBusyId !== null}
                    className="flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                  >
                    <Undo2 className="w-3 h-3" />
                    {undoBusyId === batch.batch_id ? "…" : "Rückgängig"}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "values" && (
        <>
          <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              {(["all", "subject", "room", "klass"] as const).map((cat) => (
                <button
                  key={cat}
                  onClick={() => {
                    setCategory(cat);
                    setSkip(0);
                  }}
                  className={`px-3 py-1.5 text-sm rounded-full border ${
                    category === cat
                      ? "bg-gray-900 text-white border-gray-900"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {cat === "all" ? "Alle" : CATEGORY_LABELS[cat]}
                </button>
              ))}
              <div className="flex items-center gap-2 ml-auto">
                <Search className="w-4 h-4 text-gray-400" />
                <input
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setSkip(0);
                  }}
                  placeholder="Wert suchen…"
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg w-56"
                />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
              {stats && (
                <>
                  <span>
                    Σ <strong>{stats.total_groups}</strong> Gruppen ·{" "}
                    <strong>{stats.total_unmapped_entries}</strong> Einträge
                    unzugeordnet
                  </span>
                  <span>
                    <strong>{stats.teachers_affected}</strong> Lehrkräfte
                    betroffen
                  </span>
                  <span className="text-green-700">
                    <strong>{stats.safe_suggestions}</strong> sicher
                    vorschlagbar
                  </span>
                </>
              )}
              <button
                type="button"
                onClick={() => setCatalogOpen(true)}
                className="ml-auto px-3 py-2 text-sm text-red-700 border border-red-200 rounded-lg hover:bg-red-50"
              >
                Katalog löschen
              </button>
            </div>
          </div>

          {/* List */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {loading ? (
              <div className="p-8 flex items-center justify-center text-gray-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Lade Daten…
              </div>
            ) : !data || data.items.length === 0 ? (
              <div className="p-8 text-center text-gray-500 text-sm">
                Keine unzugeordneten Werte in dieser Auswahl — Datenqualität
                vollständig.
              </div>
            ) : (
              <table className="w-full text-sm divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">
                      Kategorie
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">
                      Wert
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">
                      Einträge
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">
                      Lehrkräfte
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">
                      Cross-Scan / Vorschlag
                    </th>
                    <th className="px-4 py-3 text-right font-medium text-gray-700"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.items.map((item) => {
                    const key = itemKey(item);
                    return (
                      <tr
                        key={key}
                        tabIndex={0}
                        className={`cursor-pointer hover:bg-blue-50 ${item.is_null_value ? "bg-gray-50" : ""}`}
                        onClick={() => setEvidenceItem(item)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setEvidenceItem(item);
                          }
                        }}
                      >
                        <td className="px-4 py-3">
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${CATEGORY_COLORS[item.category]}`}
                          >
                            {CATEGORY_LABELS[item.category]}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono">
                          {item.is_null_value ? (
                            <span className="text-gray-400 italic">
                              (ohne {CATEGORY_LABELS[item.category]})
                            </span>
                          ) : (
                            item.value
                          )}
                          {item.cross_fields.length > 0 && (
                            <span
                              className="ml-2 inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5"
                              title={`Wert taucht auch auf in: ${item.cross_fields.join(", ")}`}
                            >
                              <AlertTriangle className="w-3 h-3" />
                              auch:{" "}
                              {item.cross_fields
                                .map(
                                  (f) =>
                                    CATEGORY_LABELS[f as DataQualityCategory]
                                )
                                .join(", ")}
                            </span>
                          )}
                          {item.split?.suspicious && (
                            <span className="ml-2 inline-flex items-center gap-1 text-xs text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
                              <AlertTriangle className="w-3 h-3" />
                              verdächtig:{" "}
                              {item.split.reason ===
                              "ambiguous_split_multiple_decompositions"
                                ? "mehrdeutige Auftrennung"
                                : "verschmolzen?"}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">{item.entry_count}</td>
                        <td className="px-4 py-3">
                          {item.teacher_count}
                          {item.sample_teachers.length > 0 && (
                            <div className="text-xs text-gray-400">
                              {item.sample_teachers.filter(Boolean).join(", ")}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {item.suggestion?.strategy ===
                          "ambiguous_canonical" ? (
                            <span className="text-xs text-amber-700">
                              mehrere kanonische Kandidaten — manuell wählen
                            </span>
                          ) : item.suggestion ? (
                            <div className="flex items-center gap-2">
                              <ChevronRight className="w-4 h-4 text-gray-400" />
                              <span className="font-medium">
                                {item.suggestion.target_name}
                              </span>
                              <span className="flex items-center gap-1 text-xs text-gray-500">
                                {item.suggestion.confidence.toFixed(2)}
                                <span className="w-14 h-1.5 bg-gray-200 rounded-full overflow-hidden inline-block">
                                  <span
                                    className={`block h-full ${confidenceColor(item.suggestion.confidence)}`}
                                    style={{
                                      width: `${Math.round(item.suggestion.confidence * 100)}%`,
                                    }}
                                  />
                                </span>
                              </span>
                              {item.suggestion.strategy ===
                                "alias_match_unconfirmed" && (
                                <span className="text-xs text-gray-500">
                                  (Alias unbestätigt — nicht bulk-tauglich)
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs">
                              manuell zuordnen
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-xs font-semibold text-blue-700 whitespace-nowrap">
                          Ansehen
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {data && data.total > limit && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 text-sm text-gray-600">
                <span>
                  Seite {currentPage} von {totalPages} · {data.total} Gruppen
                </span>
                <div className="space-x-2">
                  <button
                    onClick={() => setSkip(Math.max(0, skip - limit))}
                    disabled={skip === 0}
                    className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50"
                  >
                    Zurück
                  </button>
                  <button
                    onClick={() => setSkip(skip + limit)}
                    disabled={skip + limit >= data.total}
                    className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50"
                  >
                    Weiter
                  </button>
                </div>
              </div>
            )}
          </div>
          {catalogOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
              <div className="bg-white rounded-lg p-4 max-w-md space-y-3">
                <p className="text-sm text-gray-800">
                  Fächer, Räume, Klassen und den Token-Speicher dieser Schule
                  löschen? Qualifikationsmatrix, Schul-Regex, Zeiten, Lektionen
                  und andere Schulen bleiben.
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setCatalogOpen(false)}
                    className="px-3 py-2 text-sm border rounded-lg"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={() => void handleCatalogWipe()}
                    disabled={catalogWipeBusy}
                    className="px-3 py-2 text-sm text-white bg-red-600 rounded-lg disabled:opacity-50"
                  >
                    Löschen
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {tab === "tokens" && (
        <div className="space-y-4">
          <TokenCleanupTab
            batchId={findings?.batch_id ?? findings?.import_batch_id ?? null}
            refreshTick={tokenTick}
            onRequestMemoryWipe={() => setMemoryOpen(true)}
            onSuccess={(message, batchId) => {
              setSuccess(message);
              if (batchId) setLastBatchId(batchId);
              void fetchBatches();
              void fetchKenntnis();
            }}
            onError={setError}
          />
          {memoryOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
              <div className="bg-white rounded-lg p-4 max-w-md space-y-3">
                <p className="text-sm text-gray-800">
                  Token-Speicher dieser Schule löschen? Gespeicherte
                  Zuordnungen, Splits und offene Import-Shops gehen weg.
                  Katalog, Qualifikationsmatrix, Schul-Regex und Lektionen
                  bleiben.
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setMemoryOpen(false)}
                    className="px-3 py-2 text-sm border rounded-lg"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={() => void handleMemoryWipe()}
                    disabled={memoryWipeBusy}
                    className="px-3 py-2 text-sm text-white bg-red-600 rounded-lg disabled:opacity-50"
                  >
                    Löschen
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "class_concepts" && (
        <CodeConceptTrainer
          kind="class"
          onError={setError}
          onSuccess={setSuccess}
        />
      )}
      {tab === "room_concepts" && (
        <CodeConceptTrainer
          kind="room"
          onError={setError}
          onSuccess={setSuccess}
        />
      )}
      {evidenceItem && (
        <CatalogEvidencePanel
          key={`${evidenceItem.category}:${evidenceItem.value ?? ""}:${evidenceItem.is_null_value}`}
          item={evidenceItem}
          batchId={findings?.batch_id ?? findings?.import_batch_id ?? null}
          findingsReady={findingsReady}
          onClose={() => setEvidenceItem(null)}
          onTeacherReviewed={(teacherId, confirmed) => {
            const reviewed = evidenceItem;
            setData((prev) => {
              if (!prev || !reviewed) return prev;
              return {
                ...prev,
                items: prev.items.map((item) =>
                  item.category === reviewed.category &&
                  item.value === reviewed.value &&
                  item.is_null_value === reviewed.is_null_value
                    ? {
                        ...item,
                        teachers: item.teachers.map((t) =>
                          t.id === teacherId
                            ? { ...t, manually_confirmed: confirmed }
                            : t
                        ),
                      }
                    : item
                ),
              };
            });
            setEvidenceItem((prev) => {
              if (
                !prev ||
                prev.category !== reviewed.category ||
                prev.value !== reviewed.value ||
                prev.is_null_value !== reviewed.is_null_value
              ) {
                return prev;
              }
              return {
                ...prev,
                teachers: prev.teachers.map((t) =>
                  t.id === teacherId
                    ? { ...t, manually_confirmed: confirmed }
                    : t
                ),
              };
            });
          }}
        />
      )}
      {systemModalTeacher && (
        <SystemTimetableModal
          teacherId={systemModalTeacher}
          batchId={findings?.batch_id}
          onFindingClick={(entryId) => {
            const t = (findings?.teachers || []).find(
              (row) => row.teacher_id === systemModalTeacher
            );
            const f = t?.findings.find((item) => item.entry_id === entryId);
            if (t && f) {
              setSystemModalTeacher(null);
              openFinding(
                t.teacher_id,
                t.name || t.code || t.teacher_id,
                f as ScanFinding
              );
              return true;
            }
            return false;
          }}
          onPlanConfirmed={(teacherId, confirmed) => {
            if (confirmed) {
              setSystemModalTeacher(null);
              setFindings((prev) => {
                if (!prev) return prev;
                return {
                  ...prev,
                  teachers: prev.teachers.map((row) =>
                    row.teacher_id !== teacherId
                      ? row
                      : {
                          ...row,
                          red: 0,
                          yellow: 0,
                          green: row.green + row.red + row.yellow,
                          findings: [],
                          manually_confirmed: true,
                        }
                  ),
                };
              });
            }
            void fetchFindings();
          }}
          onClose={() => setSystemModalTeacher(null)}
        />
      )}
      {pdfModalTeacher && (
        <PdfTimetableModal
          teacherId={pdfModalTeacher}
          batchId={findings?.batch_id}
          onClose={() => setPdfModalTeacher(null)}
        />
      )}
      {fixPanel && (
        <FindingFixPanel
          teacherId={fixPanel.teacherId}
          teacherName={fixPanel.teacherName}
          finding={fixPanel.finding}
          batchId={findings?.batch_id ?? null}
          importAvailable={!!findings?.import_available}
          onClose={() => setFixPanel(null)}
          onApplied={async (result) => {
            if (result.undo_id) {
              setLastSpanUndoId(result.undo_id);
              setLastBatchId(null);
            } else if (result.batch_id) {
              setLastBatchId(result.batch_id);
              setLastSpanUndoId(null);
            }
            setSuccess(
              `${result.affected_rows} Einträge geändert — Rückgängig möglich`
            );
            const current = fixPanel;
            try {
              const updated = await timetableDataQualityApi.getFindings();
              setFindings(updated);
              if (current) {
                setFixPanel(
                  pickNextFinding(
                    updated.teachers || [],
                    current.teacherId,
                    current.finding.entry_id
                  )
                );
              } else {
                setFixPanel(null);
              }
            } catch (err) {
              setFixPanel(null);
              setError(
                extractErrorMessage(err, {
                  loadError: "Befunde konnten nicht geladen werden",
                })
              );
            }
            void Promise.all([fetchData(), fetchBatches()]);
          }}
          onNext={openNextFinding}
          onReimport={() => {
            setFixPanel(null);
            setReimportConfirm(true);
          }}
        />
      )}
      {reimportConfirm && !forceRestart && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 space-y-4">
            <h3 className="text-lg font-semibold">Neu importieren</h3>
            <p className="text-sm text-gray-600">
              Stundenplan aus dem PDF des letzten Imports neu einlesen. Der
              letzte Import-Stand wird neu gelesen.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setReimportConfirm(false)}
                className="px-4 py-2 text-sm border rounded-lg"
              >
                Abbrechen
              </button>
              <button
                onClick={() => {
                  setReimportConfirm(false);
                  setShowValidity(true);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg"
              >
                Weiter
              </button>
            </div>
          </div>
        </div>
      )}
      {forceRestart && !importStarting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 space-y-4">
            <h3 className="text-lg font-semibold">Import läuft bereits</h3>
            <p className="text-sm text-gray-600">
              Trotzdem neu starten und den letzten Stand ersetzen?
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setForceRestart(false)}
                className="px-4 py-2 text-sm border rounded-lg"
              >
                Abbrechen
              </button>
              <button
                onClick={() =>
                  void startReimport(
                    validityRef.current.from,
                    validityRef.current.to,
                    true
                  )
                }
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg"
              >
                Ersetzen
              </button>
            </div>
          </div>
        </div>
      )}
      {showValidity && (
        <ImportValidityDialog
          isOpen={showValidity}
          title="Gültigkeit des Neuimports"
          description="Zeitraum für die neu eingelesenen Lektionen."
          onConfirm={(from, to) => void startReimport(from, to, forceRestart)}
          onCancel={() => {
            setShowValidity(false);
            setForceRestart(false);
          }}
        />
      )}
      {importStarting && (
        <ProgressDialog title="Import wird gestartet…" current={0} total={0} />
      )}
      {importJob && (
        <BulkImportProgress
          jobId={importJob.jobId}
          batchId={importJob.batchId}
          onCompleted={(batchId) => {
            setImportJob(null);
            void (async () => {
              try {
                const batchData =
                  await timetableImportV2Api.getBatchStatus(batchId);
                if (batchData.status === "pending_bulk_shop") {
                  setShopSkippedBatchId(null);
                  setShopBatchId(batchId);
                  return;
                }
              } catch {
                /* persist without shop */
              }
              setShopSkippedBatchId(null);
              void fetchFindings();
            })();
          }}
          onCancel={() => setImportJob(null)}
        />
      )}
      {shopBatchId && (
        <BulkShopDialog
          batchId={shopBatchId}
          onDone={() => {
            setShopBatchId(null);
            setShopSkippedBatchId(null);
            void fetchFindings();
          }}
          onEmpty={() => {
            setShopBatchId(null);
            setShopSkippedBatchId(null);
            void fetchFindings();
          }}
          onCancel={() => {
            setShopSkippedBatchId(shopBatchId);
            setShopBatchId(null);
          }}
        />
      )}
      {kenntnisOpen && kenntnis && (
        <KenntnisSealModal
          preview={kenntnis}
          busy={kenntnisBusy}
          onClose={() => setKenntnisOpen(false)}
          onSeal={async (groups: KenntnisGroup[]) => {
            setKenntnisBusy(true);
            try {
              const result = await timetableDataQualityApi.sealKenntnis(groups);
              setKenntnis(result.preview);
              setKenntnisOpen(false);
              setSuccess(`Kenntnis gespeichert (${result.written})`);
              setTokenTick((tick) => tick + 1);
              void fetchData();
              void fetchFindings();
            } catch (err) {
              setError(
                extractErrorMessage(err, {
                  loadError: "Kenntnis speichern fehlgeschlagen",
                })
              );
            } finally {
              setKenntnisBusy(false);
            }
          }}
        />
      )}
    </div>
  );
}
